import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ImagePlus, Send, X } from 'lucide-react';
import { useCRM } from '../../../utils/CRMContext';
import {
  addTripPlannerMessage,
  applyTripPlanDraftToCanvas,
  subscribeTripPlannerMessages,
} from '../../../utils/peopleTravelFirestore';
import { planTripFromPrompt } from '../../../utils/peopleTravelPlanService';
import {
  formatCanvasSelectionLabel,
  type TravelCanvasSelection,
} from '../../../utils/peopleTravelCanvasGraph';
import { filterPeopleTravelTeamMembers } from '../../../utils/peopleTravelTeamMembers';
import {
  TRAVEL_PLAN_INPUT_MAX_FILES,
  appendLocalPlanFiles,
  filesFromClipboard,
  revokeLocalPlanFiles,
  uploadTravelPlanInputs,
  type TravelPlanLocalFile,
} from '../../../utils/travelPlannerInput';
import {
  buildTripPlanConversationPrompt,
  resolveTripPlanChainMode,
  type TripPlanChainMode,
  type TripPlanChatTurn,
} from '../../../utils/tripPlanConversation';
import type {
  PeopleTravelEvent,
  PeopleTravelLeg,
  PeopleTravelLocation,
  PeopleTravelTrip,
} from '../../../types';

type Props = {
  trip: PeopleTravelTrip;
  ownerUserId: string;
  readOnly?: boolean;
  selection: TravelCanvasSelection | null;
  locations: PeopleTravelLocation[];
  events: PeopleTravelEvent[];
  legs: PeopleTravelLeg[];
  attachSignal?: number;
  rebuildSignal?: number;
};

const PeopleTravelPlannerDock: React.FC<Props> = ({
  trip,
  ownerUserId,
  readOnly,
  selection,
  locations,
  events,
  legs,
  attachSignal,
  rebuildSignal = 0,
}) => {
  const { crmUsers } = useCRM();
  const teamMembers = useMemo(() => filterPeopleTravelTeamMembers(crmUsers), [crmUsers]);
  const [turns, setTurns] = useState<TripPlanChatTurn[]>([]);
  const [composer, setComposer] = useState('');
  const [files, setFiles] = useState<TravelPlanLocalFile[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chainMode, setChainMode] = useState<TripPlanChainMode>('build');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const isEmptyChain = locations.length === 0 && events.length === 0 && legs.length === 0;

  useEffect(() => {
    return subscribeTripPlannerMessages(trip.id, setTurns);
  }, [trip.id]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, sending]);

  useEffect(() => {
    if (attachSignal && attachSignal > 0) fileRef.current?.click();
  }, [attachSignal]);

  useEffect(() => {
    if (isEmptyChain) setChainMode('build');
    else if (selection) setChainMode('edit');
  }, [isEmptyChain, selection]);

  useEffect(() => {
    if (!rebuildSignal) return;
    setChainMode('rebuild');
    inputRef.current?.focus();
  }, [rebuildSignal]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const addFiles = (incoming: File[]) => {
    if (!incoming.length) return;
    setFiles(prev => appendLocalPlanFiles(prev, incoming));
  };

  const removeFile = (id: string) => {
    setFiles(prev => {
      const row = prev.find(f => f.id === id);
      if (row) URL.revokeObjectURL(row.previewUrl);
      return prev.filter(f => f.id !== id);
    });
  };

  const activeMode = resolveTripPlanChainMode({
    chainMode: isEmptyChain ? 'build' : chainMode,
    locations,
    events,
    legs,
  });
  const scopeLabel =
    activeMode === 'build'
      ? 'Build the chain'
      : activeMode === 'rebuild'
        ? 'Rebuild the whole chain'
        : formatCanvasSelectionLabel(selection, locations, events, legs);

  const handleSend = async () => {
    const text = composer.trim();
    if (sending || readOnly || (!text && files.length === 0)) return;
    setSending(true);
    setError(null);
    const userTurn: TripPlanChatTurn = {
      id: `user_${Date.now()}`,
      role: 'user',
      text: text || (files.length ? `Sent ${files.length} screenshot${files.length === 1 ? '' : 's'}.` : ''),
      imageCount: files.length,
      createdAt: Date.now(),
    };
    const priorTurns = turns;
    setComposer('');
    try {
      await addTripPlannerMessage(trip.id, userTurn);
      const attachments = await uploadTravelPlanInputs(files);
      revokeLocalPlanFiles(files);
      setFiles([]);
      const prompt = buildTripPlanConversationPrompt({
        turns: priorTurns,
        latestText: text,
        imageCount: attachments.length,
        teamMembers,
        selection: activeMode === 'edit' ? selection : null,
        locations,
        events,
        legs,
        chainMode: activeMode,
      });
      const result = await planTripFromPrompt({
        prompt,
        ownerUserId,
        teamMembers,
        attachments,
        tripId: trip.id,
        selection: activeMode === 'edit' ? selection : null,
        canvasSnapshot: { locations, events, legs },
      });
      setChainMode(isEmptyChain ? 'build' : 'edit');
      const reply =
        result.draft.summary ||
        (result.draft.openQuestions?.length
          ? result.draft.openQuestions.join('\n')
          : 'Updated. Cards on the board refresh as I write them.');
      await addTripPlannerMessage(trip.id, {
        id: `planner_${Date.now()}`,
        role: 'planner',
        text: reply,
        createdAt: Date.now(),
      });
      try {
        await applyTripPlanDraftToCanvas(trip.id, result.draft);
      } catch (applyErr) {
        setError(
          applyErr instanceof Error
            ? applyErr.message
            : 'Planner replied, but the board could not be updated.',
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const canSend = !sending && !readOnly && Boolean(composer.trim() || files.length);

  return (
    <aside className="people-travel-planner-popout" aria-label="Trip planner">
      <div className="people-travel-planner-dock-chat">
        <div className="people-travel-planner-dock-head">
          <strong>Trip planner</strong>
          <span className="people-hub-muted">{scopeLabel}</span>
        </div>
        <div className="people-travel-chat-scroll" ref={scrollRef}>
          {turns.length === 0 && !sending && (
            <div className="people-travel-chat-empty">
              {isEmptyChain ? (
                <>
                  <p>Start with the overall chain — who is going, which places, and how you travel between them.</p>
                  <p className="people-hub-muted">
                    The board will draw location clusters, travel legs, and events. After that you can rebuild the
                    whole chain or change one section.
                  </p>
                </>
              ) : (
                <p>
                  Select a card to change that section, or use Rebuild chain to redo the whole trip. New cards show
                  up on the board as soon as they are written.
                </p>
              )}
            </div>
          )}
          {turns.map(turn => (
            <div
              key={turn.id}
              className={`people-travel-chat-msg ${
                turn.role === 'user' ? 'people-travel-chat-msg-user' : 'people-travel-chat-msg-planner'
              }`}
            >
              <div className="people-travel-chat-bubble">{turn.text}</div>
              {turn.imageCount ? (
                <div className="people-travel-chat-meta">
                  {turn.imageCount} screenshot{turn.imageCount === 1 ? '' : 's'}
                </div>
              ) : null}
            </div>
          ))}
          {sending && (
            <div className="people-travel-chat-msg people-travel-chat-msg-planner">
              <div className="people-travel-chat-bubble people-travel-chat-typing" aria-label="Planner is replying">
                <span />
                <span />
                <span />
              </div>
              <div className="people-travel-chat-meta">This can take a few minutes.</div>
            </div>
          )}
        </div>
        {error && <p className="people-travel-chat-error">{error}</p>}
        <div
          className="people-travel-chat-composer"
          onDragOver={e => e.preventDefault()}
          onDrop={e => {
            e.preventDefault();
            if (!sending && !readOnly) addFiles(filesFromClipboard(e.dataTransfer));
          }}
        >
          {files.length > 0 && (
            <div className="people-travel-planner-thumbs" aria-label="Attached screenshots">
              {files.map(file => (
                <div key={file.id} className="people-travel-planner-thumb">
                  <img src={file.previewUrl} alt={file.name} />
                  <button
                    type="button"
                    className="people-travel-planner-thumb-remove"
                    aria-label={`Remove ${file.name}`}
                    disabled={sending}
                    onClick={() => removeFile(file.id)}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="people-travel-chat-compose-row">
            <button
              type="button"
              className="people-travel-chat-attach"
              disabled={sending || readOnly || files.length >= TRAVEL_PLAN_INPUT_MAX_FILES}
              onClick={() => fileRef.current?.click()}
              aria-label="Add screenshot"
            >
              <ImagePlus size={16} />
            </button>
            <textarea
              ref={inputRef}
              className="people-travel-chat-input"
              rows={2}
              disabled={sending || readOnly}
              value={composer}
              placeholder={
                activeMode === 'build'
                  ? 'Who, where, and how you get there…'
                  : activeMode === 'rebuild'
                    ? 'How should the whole chain change?'
                    : `Change ${scopeLabel.toLowerCase()}…`
              }
              onChange={e => setComposer(e.target.value)}
              onPaste={e => {
                const pasted = filesFromClipboard(e.clipboardData);
                if (!pasted.length) return;
                e.preventDefault();
                addFiles(pasted);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
            />
            <button
              type="button"
              className="people-travel-chat-send"
              disabled={!canSend}
              onClick={() => void handleSend()}
              aria-label="Send"
            >
              <Send size={15} />
            </button>
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={e => {
            addFiles(Array.from(e.target.files || []));
            e.target.value = '';
          }}
        />
      </div>
    </aside>
  );
};

export default PeopleTravelPlannerDock;
